import * as React from "react";
import { useLocation } from "wouter";
import {
  Menu,
  ArrowLeft,
  Sparkles,
  Brain,
  Clock,
  Calendar,
  Paperclip,
  Lock,
  Archive,
  Trash2,
  Palette,
  Settings,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const MENU_ITEMS = [
  { icon: Sparkles, label: "Upgrade", iconColor: "text-amber-500" },
  { icon: Brain, label: "AI Memory" },
  { icon: Clock, label: "Today's Memory" },
  { icon: Calendar, label: "Calendar" },
  { icon: Paperclip, label: "Attachments" },
  { icon: Lock, label: "Private Vault" },
  { icon: Archive, label: "Archive" },
  { icon: Trash2, label: "Trash" },
  { icon: Palette, label: "Appearance" },
  { icon: Settings, label: "Settings" },
];

export function Navigation() {
  const [location, setLocation] = useLocation();
  const isSearchPage = location.startsWith("/search");
  const [hasCustomBg, setHasCustomBg] = React.useState(false);
  React.useEffect(() => {
    setHasCustomBg(
      location === "/" && !!localStorage.getItem("rememly-capture-background"),
    );
  }, [location]);

  return (
    <nav
      className={
        hasCustomBg
          ? "bg-transparent sticky top-0 z-50"
          : "bg-background/80 backdrop-blur-sm sticky top-0 z-50"
      }
    >
      <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-3">
        {isSearchPage ? (
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) {
                window.history.back();
              } else {
                setLocation("/");
              }
            }}
            className="h-10 w-10 shrink-0 rounded-full bg-background border shadow-sm flex items-center justify-center hover:bg-muted transition-colors"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        ) : (
          <>
            <span className="text-3xl font-bold tracking-wide bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Rememly
            </span>
            <div className="flex-1" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  disabled={location !== "/"}
                  className={`h-10 w-10 shrink-0 rounded-full bg-background border shadow-sm flex items-center justify-center transition-colors ${
                    location !== "/"
                      ? "opacity-40 pointer-events-none"
                      : "hover:bg-muted"
                  }`}
                  aria-label="Menu"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                collisionPadding={8}
                className="rounded-2xl p-1 w-52 shadow-lg border-none max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto"
              >
                {/* Placeholder actions — not wired up yet */}
                {MENU_ITEMS.map(({ icon: Icon, label, iconBg, iconColor }: { icon: typeof Sparkles; label: string; iconBg?: string; iconColor?: string }, i) => (
                  <DropdownMenuItem
                    key={label}
                    onClick={
                      label === "Appearance"
                        ? () => {
                            if (location !== "/appearance") {
                              setLocation("/appearance");
                            }
                          }
                        : label === "Calendar"
                          ? () => {
                              if (location !== "/calendar") {
                                setLocation("/calendar");
                              }
                            }
                          : label === "Today's Memory"
                            ? () => {
                                if (location !== "/today-memory") {
                                  setLocation("/today-memory");
                                }
                              }
                            : label === "Archive"
                              ? () => {
                                  if (location !== "/archive") {
                                    setLocation("/archive");
                                  }
                                }
                            : label === "Trash"
                              ? () => {
                                  if (location !== "/trash") {
                                    setLocation("/trash");
                                  }
                                }
                              : undefined
                    }
                    className={
                      "rounded-none px-1.5 py-2 gap-2 text-sm font-medium focus:bg-muted/50" +
                      (i !== MENU_ITEMS.length - 1
                        ? " border-b border-border/60"
                        : "")
                    }
                  >
                    <div
                      className={
                        iconBg
                          ? `h-7 w-7 shrink-0 rounded-full ${iconBg} flex items-center justify-center`
                          : "h-7 w-7 shrink-0 flex items-center justify-center"
                      }
                    >
                      <Icon
                        className={`h-3.5 w-3.5 ${iconColor ?? "text-muted-foreground"}`}
                      />
                    </div>
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
    </nav>
  );
}
